import { useState, useCallback } from 'react';
import { SERVICES_KNOWLEDGE } from '@/src/data/servicesKnowledge';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface UseServicesRAGReturn {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (message: string) => Promise<void>;
  clearMessages: () => void;
}

const SYSTEM_PROMPT = `Eres un asistente técnico experto de IntelliGrow AI Agency. Tu trabajo es ayudar a los usuarios a entender nuestros servicios de IA y guiarlos hacia la solución correcta.

INSTRUCCIONES:
1. Responde SOLO basándote en la información proporcionada en el contexto
2. Sé conciso pero informativo (máximo 3-4 párrafos)
3. Si preguntan por precios específicos, menciona rangos generales y sugiere agendar llamada
4. Siempre termina sugiriendo el siguiente paso: agendar llamada o contactar
5. Si preguntan algo fuera de nuestros servicios, redirige amablemente a nuestros servicios
6. Usa un tono profesional pero cercano
7. Incluye enlaces relevantes cuando sea apropiado

CONTEXTO DE SERVICIOS:
${SERVICES_KNOWLEDGE}

Recuerda: Tu objetivo es ayudar al usuario a encontrar la mejor solución y motivarlo a contactarnos.`;

export function useServicesRAG(): UseServicesRAGReturn {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: '¡Hola! 👋 Soy el asistente técnico de IntelliGrow. Puedo ayudarte a entender nuestros servicios de IA y encontrar la mejor solución para tu empresa. ¿En qué puedo ayudarte?',
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(async (userMessage: string) => {
    if (!userMessage.trim()) return;

    // Add user message
    const newUserMessage: Message = { role: 'user', content: userMessage };
    setMessages((prev) => [...prev, newUserMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
      
      if (!apiKey || apiKey === 'PLACEHOLDER_API_KEY') {
        throw new Error('API key no configurada');
      }

      // Build conversation history
      const conversationHistory = messages
        .map((msg) => `${msg.role === 'user' ? 'Usuario' : 'Asistente'}: ${msg.content}`)
        .join('\n\n');

      const prompt = `${SYSTEM_PROMPT}

HISTORIAL DE CONVERSACIÓN:
${conversationHistory}

Usuario: ${userMessage}

Asistente:`;

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: prompt,
                  },
                ],
              },
            ],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 1024,
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Error al comunicarse con la API');
      }

      const data = await response.json();
      const assistantMessage = data.candidates[0]?.content?.parts[0]?.text || 
        'Lo siento, no pude procesar tu pregunta. ¿Podrías reformularla?';

      // Add assistant message
      const newAssistantMessage: Message = {
        role: 'assistant',
        content: assistantMessage,
      };
      setMessages((prev) => [...prev, newAssistantMessage]);
    } catch (err) {
      console.error('Error in RAG chat:', err);
      setError('Error al procesar tu mensaje. Por favor, intenta de nuevo.');
      
      // Add error message
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Disculpa, tuve un problema técnico. Mientras tanto, puedes contactarnos directamente:\n\n📧 admin@intelligrow.shop\n📅 https://calendly.com/intelligroow-nvmb/30min',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [messages]);

  const clearMessages = useCallback(() => {
    setMessages([
      {
        role: 'assistant',
        content: '¡Hola! 👋 Soy el asistente técnico de IntelliGrow. Puedo ayudarte a entender nuestros servicios de IA y encontrar la mejor solución para tu empresa. ¿En qué puedo ayudarte?',
      },
    ]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
  };
}
